import React, { useEffect, useState } from "react";
import {
  TagIcon,
  BriefcaseIcon,
  CalendarIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ---------- Helpers ---------- */
const formatDate = (d) => {
  if (!d) return null;
  if (d.seconds) return new Date(d.seconds * 1000).toLocaleDateString();
  if (d._seconds) return new Date(d._seconds * 1000).toLocaleDateString();
  if (typeof d === "number") return new Date(d).toLocaleDateString();
  if (typeof d === "string") {
    const dt = new Date(d);
    if (!isNaN(dt.getTime())) return dt.toLocaleDateString();
  }
  return null;
};

const Pill = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="group flex items-start gap-2 rounded-xl border border-gray-200/80 bg-white px-3 py-2 shadow-sm hover:shadow transition-all hover:-translate-y-0.5">
      <div className="mt-0.5 h-8 w-8 shrink-0 rounded-lg bg-neutral-50 grid place-items-center border border-gray-200">
        <Icon className="h-4 w-4 text-gray-700" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
        <div className="text-sm text-gray-900">{value}</div>
      </div>
    </div>
  );
};

/* ---------- Avatar (with initials fallback) ---------- */
const Avatar = ({ src, name, size = "h-9 w-9" }) => {
  const initials = (name || "U")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  if (src) {
    return (
      <img
        src={src}
        alt={name || "Profile"}
        className={`${size} rounded-full object-cover ring-1 ring-black/5`}
        onError={(e) => (e.currentTarget.src = "")} // fallback to initials
      />
    );
  }

  return (
    <div
      className={`${size} rounded-full bg-neutral-100 text-gray-700 grid place-items-center text-xs font-semibold ring-1 ring-black/5`}
    >
      {initials}
    </div>
  );
};

/* ---------- ProjectImage Fallback ---------- */
const categoryEmoji = {
  Tech: "💻",
  Art: "🎨",
  Music: "🎵",
  Gaming: "🎮",
  Food: "🍜",
  Fashion: "👗",
  Science: "🔬",
  Content: "✍️",
  Craft: "🛠️",
};

const ProjectImage = ({ src, title, category, id }) => {
  if (src) {
    return (
      <img
        src={src}
        alt={title}
        className="h-full w-full object-cover"
        onError={(e) => (e.currentTarget.src = "")} // fallback to custom UI
      />
    );
  }

  // pick emoji or fallback letter
  const emoji = categoryEmoji[category];
  const firstLetter = (title || "P")[0].toUpperCase();

  // gradient variations (deterministic by id)
  const gradients = [
    "bg-gradient-to-br from-sky-400 to-indigo-600",
    "bg-gradient-to-br from-pink-400 to-rose-600",
    "bg-gradient-to-br from-emerald-400 to-teal-600",
    "bg-gradient-to-br from-amber-400 to-orange-600",
  ];
  const pick = id
    ? gradients[id.charCodeAt(0) % gradients.length]
    : gradients[0];

  return (
    <div
      className={`h-full w-full flex items-center justify-center ${pick} text-white`}
    >
      <span className="text-4xl font-bold drop-shadow-sm">
        {emoji || firstLetter}
      </span>
    </div>
  );
};

/* ---------- Main Card ---------- */
const ProjectInfoCard = ({ project = {} }) => {
  const {
    id,
    title,
    oneLiner,
    description,
    image,
    category,
    tags = [],
    openRoles = [],
    createdAt,
    deadline,
    owner,
    teamMembers = [],
  } = project;

  const [ownerProfile, setOwnerProfile] = useState(null);
  const [memberProfiles, setMemberProfiles] = useState([]);

  // 🔹 Fetch owner profile
  useEffect(() => {
    const fetchOwner = async () => {
      if (!owner) return;
      try {
        const res = await fetch(`${BACKEND_URL}/user/${encodeURIComponent(owner)}`);
        if (!res.ok) throw new Error("Failed to fetch owner profile");
        const data = await res.json();
        setOwnerProfile(data);
      } catch (err) {
        console.error("❌ Failed to fetch owner:", err);
      }
    };
    fetchOwner();
  }, [owner]);

  // 🔹 Fetch team member profiles
  useEffect(() => {
    const fetchMembers = async () => {
      if (!teamMembers?.length) return;
      try {
        const profiles = await Promise.all(
          teamMembers.map(async (m) => {
            try {
              const res = await fetch(`${BACKEND_URL}/user/${encodeURIComponent(m.email)}`);
              if (!res.ok) throw new Error("Failed to fetch member profile");
              const data = await res.json();
              return { ...m, ...data }; // merge DB profile + role
            } catch (err) {
              console.error("❌ Failed to fetch member:", m.email, err);
              return m;
            }
          })
        );

        // ✅ Deduplicate by email and merge roles
        const merged = Object.values(
          profiles.reduce((acc, m) => {
            if (!m.email) return acc;
            if (!acc[m.email]) {
              acc[m.email] = { ...m, roles: [] };
            }
            if (m.role && !acc[m.email].roles.includes(m.role)) {
              acc[m.email].roles.push(m.role);
            }
            return acc;
          }, {})
        );
        setMemberProfiles(merged);
      } catch (err) {
        console.error("❌ Error fetching team members:", err);
      }
    };
    fetchMembers();
  }, [teamMembers]);

  return (
    <div className="relative">
      <div className="rounded-2xl p-[1px] bg-gradient-to-tr from-sky-200/60 via-fuchsia-200/60 to-emerald-200/60">
        <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-black/5">
          {/* Header */}
          <div className="mb-4">
            <div className="h-40 w-full overflow-hidden rounded-xl bg-neutral-100">
              <ProjectImage
                src={image}
                title={title}
                category={category}
                id={id}
              />
            </div>
            <div className="mt-3">
              <h2 className="text-lg font-semibold text-gray-900 leading-6">
                {title || "Untitled Project"}
              </h2>
              {oneLiner && <p className="text-sm text-gray-600 mt-1">{oneLiner}</p>}
            </div>
          </div>

          {/* Quick facts */}
          <div className="grid grid-cols-1 gap-2">
            <Pill icon={TagIcon} label="Category" value={category} />
            {tags && (
    <Pill
      icon={SparklesIcon}
      label="Tags"
      value={
        Array.isArray(tags)
          ? tags.join(", ")
          : typeof tags === "string"
          ? tags.split(",").map((t) => t.trim()).join(", ")
          : ""
      }
    />
  )}
            {createdAt && <Pill icon={CalendarIcon} label="Created" value={formatDate(createdAt)} />}
            {deadline && <Pill icon={CalendarIcon} label="Deadline" value={formatDate(deadline)} />}

            {/* 🔹 Owner */}
            {ownerProfile && (
              <a
                href={`/${ownerProfile.username}/profile`}
                className="group flex items-center gap-3 rounded-xl border border-gray-200/80 bg-white px-3 py-2 shadow-sm hover:shadow-md transition"
              >
                <Avatar
                  src={ownerProfile.profileImage}
                  name={`${ownerProfile.firstName} ${ownerProfile.lastName}`}
                  size="h-9 w-9"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-wide text-gray-500">
                    Owner
                  </div>
                  <div className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600">
                    {ownerProfile.firstName} {ownerProfile.lastName}
                  </div>
                </div>
              </a>
            )}
          </div>

          {/* Description */}
          {description && (
            <div className="mt-5">
              <div className="text-xs font-bold uppercase text-gray-400 mb-2">Description</div>
              <div className="text-sm text-gray-800 whitespace-pre-wrap leading-5">{description}</div>
            </div>
          )}

          {/* Open Roles */}
          {openRoles.length > 0 && (
            <div className="mt-5">
              <div className="text-xs font-bold uppercase text-gray-400 mb-2">Open Roles</div>
              <div className="space-y-2">
                {openRoles.map((role, i) => (
                  <div
                    key={i}
                    className="group relative rounded-xl border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5"
                  >
                    <div className="absolute left-0 top-0 h-full w-1 rounded-l-xl bg-gradient-to-b from-sky-400 to-emerald-400 opacity-70" />
                    <div className="pl-3">
                      <div className="flex items-center gap-2">
                        <BriefcaseIcon className="h-4 w-4 text-gray-700" />
                        <div className="font-medium text-gray-900">{role.roleTitle}</div>
                      </div>
                      {role.description && <div className="text-sm text-gray-700 mt-1">{role.description}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Team Members */}
          {memberProfiles.length > 0 && (
            <div className="mt-5">
              <div className="text-xs font-bold uppercase text-gray-400 mb-2">Team Members</div>
              <div className="flex flex-col gap-2">
                {memberProfiles.map((m, i) => (
                  <a
                    key={i}
                    href={`/${m.username}/profile`}
                    className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-2 shadow-sm hover:shadow-md transition"
                  >
                    <Avatar src={m.profileImage} name={`${m.firstName} ${m.lastName}`} size="h-8 w-8" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                        {m.firstName} {m.lastName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {m.roles?.length ? m.roles.join(" / ") : "Member"}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectInfoCard;
